import React, { useState, useContext, useEffect, useCallback } from "react";
import { makeStyles } from "tss-react/mui";
import { alpha } from "@mui/material/styles";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Avatar,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import ClbsAppBar from "../wrapper/appBar";
import SnackBarDuet from "../utils/snackBarDuet";
import { baseBackURL } from "../config/baseUrl";
import { UserContext } from "../context/userContext";
import { LanguageContext } from "../context/languageContext";
import { accountLabels } from "../config/accountLabels";
import { userDetailsLabels } from "../config/adminPanelLabels";
import { assessmentLabels } from "../config/commonLabels";

const GENERATED_PASSWORD_LENGTH = 16;
const PASSWORD_CHARS =
  "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
// bcrypt hashes at most 72 bytes and the backend rejects anything longer, so
// check bytes rather than characters: one CJK character costs three of them.
const MAX_PASSWORD_BYTES = 72;

const passwordBytes = (value) => new TextEncoder().encode(value).length;

// Convenience for the admin, who has to relay the password themselves. Uses
// crypto rather than Math.random so a generated password is not predictable
// from the ones generated before it.
function generatePassword() {
  const buffer = new Uint32Array(GENERATED_PASSWORD_LENGTH);
  window.crypto.getRandomValues(buffer);
  return Array.from(
    buffer,
    (n) => PASSWORD_CHARS[n % PASSWORD_CHARS.length]
  ).join("");
}

const EMPTY_FORM = {
  english_name: "",
  chinese_name: "",
  email: "",
  group: "",
  is_admin: false,
  password: "",
};

const EMPTY_STATUS = { success: false, failed: false };

const useStyles = makeStyles()((theme) => ({
  page: {
    maxWidth: 1080,
    margin: "auto",
    padding: "24px 24px 60px",
    [theme.breakpoints.down("sm")]: {
      padding: "16px 12px 40px",
    },
  },
  pageTitle: {
    fontSize: "2.125rem",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.5rem",
    },
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    maxWidth: "80ch",
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  createIcon: {
    // A tint of the brand green. theme.palette.success.light is a saturated
    // #4caf50 and reads as an alert rather than a surface.
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.primary.main,
    width: 38,
    height: 38,
    marginRight: theme.spacing(2),
  },
  note: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.dark,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    fontSize: "0.8125rem",
    lineHeight: 1.55,
  },
  listHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    flexWrap: "wrap",
  },
  listTitle: {
    marginRight: "auto",
  },
  groupRow: {
    backgroundColor: theme.palette.grey[100],
  },
  groupCell: {
    fontSize: "0.75rem",
    fontWeight: 500,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  selfRow: {
    backgroundColor: theme.palette.grey[50],
  },
  newRow: {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  chineseName: {
    color: theme.palette.text.secondary,
    fontSize: "0.8125rem",
  },
  tableCell: {
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.75rem",
      padding: "8px 6px",
    },
  },
  dangerBox: {
    borderLeft: `4px solid ${theme.palette.error.main}`,
    backgroundColor: alpha(theme.palette.error.main, 0.08),
    borderRadius: "0 4px 4px 0",
    padding: theme.spacing(1.5),
    margin: theme.spacing(1.5, 0, 2),
  },
  spinnerWrap: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const AccountManagement = () => {
  const { classes } = useStyles();
  const { tokenKey, userKey } = useContext(UserContext);
  const { language } = useContext(LanguageContext);
  const [token] = tokenKey;
  const [currentUser] = userKey;

  const [permission, setPermission] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [userData, setUserData] = useState([]);
  const [groups, setGroups] = useState([]);

  const [filter, setFilter] = useState({ group: "", search: "" });
  const [justCreated, setJustCreated] = useState(null);

  const [manageTarget, setManageTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [createStatus, setCreateStatus] = useState(EMPTY_STATUS);
  const [deleteStatus, setDeleteStatus] = useState(EMPTY_STATUS);
  const [updateStatus, setUpdateStatus] = useState(EMPTY_STATUS);
  const [passwordStatus, setPasswordStatus] = useState(EMPTY_STATUS);
  const [adminStatus, setAdminStatus] = useState(EMPTY_STATUS);
  // Which direction the last admin toggle went, so the snackbar says
  // "removed as admin" rather than always "added as admin".
  const [adminWasGranted, setAdminWasGranted] = useState(true);

  const authHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    }),
    [token]
  );

  const fetchUsers = useCallback(async () => {
    setFetching(true);
    try {
      const response = await fetch(baseBackURL + "/api/users/getall", {
        method: "GET",
        headers: authHeaders(),
      });
      if (!response.ok) {
        setPermission(false);
        return;
      }
      const data = await response.json();
      setPermission(true);
      setUserData(data);
      setGroups(Array.from(new Set(data.map((u) => u.group).filter(Boolean))));
    } catch (err) {
      // Network failure rather than a permission problem: keep the page up and
      // leave the list as it was instead of claiming the user is not an admin.
      console.error("Failed to load accounts", err);
    } finally {
      setFetching(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async (form) => {
    setFetching(true);
    try {
      const response = await fetch(baseBackURL + "/api/users/", {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          email: form.email.trim(),
          group: form.group.trim(),
          english_name: form.english_name.trim(),
          chinese_name: form.chinese_name.trim(),
          is_admin: form.is_admin,
          // Not trimmed: a space is a legitimate password character, and
          // silently dropping one would lock the person out of their account.
          password: form.password,
        }),
      });
      if (!response.ok) {
        setCreateStatus({ success: false, failed: true });
        return false;
      }
      setJustCreated(form.email.trim());
      setCreateStatus({ success: true, failed: false });
      await fetchUsers();
      return true;
    } catch (err) {
      console.error("Failed to create account", err);
      setCreateStatus({ success: false, failed: true });
      return false;
    } finally {
      setFetching(false);
    }
  };

  const handleDelete = async (email) => {
    setFetching(true);
    try {
      const response = await fetch(
        baseBackURL + "/api/users/" + encodeURIComponent(email),
        { method: "DELETE", headers: authHeaders() }
      );
      if (!response.ok) {
        setDeleteStatus({ success: false, failed: true });
        return;
      }
      setDeleteStatus({ success: true, failed: false });
      setDeleteTarget(null);
      await fetchUsers();
    } catch (err) {
      console.error("Failed to delete account", err);
      setDeleteStatus({ success: false, failed: true });
    } finally {
      setFetching(false);
    }
  };

  const handleUpdateGroup = async (email, newGroup) => {
    setFetching(true);
    try {
      const response = await fetch(
        baseBackURL + "/api/users/" + encodeURIComponent(email),
        {
          method: "PUT",
          headers: authHeaders(),
          // The API field is new_group. The old userInfo.js sent new_company,
          // which the backend silently ignored, so group edits never applied.
          body: JSON.stringify({ new_group: newGroup, new_password: null }),
        }
      );
      setUpdateStatus(
        response.ok
          ? { success: true, failed: false }
          : { success: false, failed: true }
      );
      if (response.ok) {
        // Keep the open dialog in step, otherwise Save stays enabled because it
        // is still comparing against the pre-save group.
        setManageTarget((prev) =>
          prev && prev.email === email ? { ...prev, group: newGroup } : prev
        );
        await fetchUsers();
      }
    } catch (err) {
      console.error("Failed to update group", err);
      setUpdateStatus({ success: false, failed: true });
    } finally {
      setFetching(false);
    }
  };

  const handleSetPassword = async (email, newPassword) => {
    setFetching(true);
    try {
      const response = await fetch(
        baseBackURL + "/api/users/" + encodeURIComponent(email),
        {
          method: "PUT",
          headers: authHeaders(),
          // new_group null leaves the group alone; the backend skips empty fields.
          body: JSON.stringify({ new_group: null, new_password: newPassword }),
        }
      );
      setPasswordStatus(
        response.ok
          ? { success: true, failed: false }
          : { success: false, failed: true }
      );
      return response.ok;
    } catch (err) {
      console.error("Failed to set password", err);
      setPasswordStatus({ success: false, failed: true });
      return false;
    } finally {
      setFetching(false);
    }
  };

  const handleToggleAdmin = async (email, makeAdmin) => {
    setFetching(true);
    setAdminWasGranted(makeAdmin);
    try {
      const path = makeAdmin ? "/api/users/setAdmin/" : "/api/users/removeAdmin/";
      const response = await fetch(
        baseBackURL + path + encodeURIComponent(email),
        { method: "GET", headers: authHeaders() }
      );
      setAdminStatus(
        response.ok
          ? { success: true, failed: false }
          : { success: false, failed: true }
      );
      if (response.ok) {
        const refreshed = await fetch(baseBackURL + "/api/users/getall", {
          method: "GET",
          headers: authHeaders(),
        });
        if (refreshed.ok) {
          const data = await refreshed.json();
          setUserData(data);
          setManageTarget(
            (prev) => data.find((u) => u.email === prev?.email) || prev
          );
        }
      }
    } catch (err) {
      console.error("Failed to change admin access", err);
      setAdminStatus({ success: false, failed: true });
    } finally {
      setFetching(false);
    }
  };

  if (!permission) {
    return (
      <ClbsAppBar>
        <PermissionDenied />
      </ClbsAppBar>
    );
  }

  return (
    <ClbsAppBar>
      <div className={classes.page}>
        <Typography variant="h4" className={classes.pageTitle} gutterBottom>
          {accountLabels.title[language]}
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          {accountLabels.subtitle[language]}
        </Typography>

        <CreatePanel
          classes={classes}
          language={language}
          groups={groups}
          onCreate={handleCreate}
          busy={fetching}
        />

        <AccountsList
          classes={classes}
          language={language}
          users={userData}
          groups={groups}
          filter={filter}
          setFilter={setFilter}
          currentEmail={currentUser?.email}
          justCreated={justCreated}
          onManage={setManageTarget}
          onDelete={setDeleteTarget}
          loading={fetching && userData.length === 0}
        />
      </div>

      <ManageDialog
        classes={classes}
        language={language}
        user={manageTarget}
        groups={groups}
        currentEmail={currentUser?.email}
        busy={fetching}
        onClose={() => setManageTarget(null)}
        onSaveGroup={handleUpdateGroup}
        onSetPassword={handleSetPassword}
        onToggleAdmin={handleToggleAdmin}
      />

      <DeleteDialog
        classes={classes}
        language={language}
        user={deleteTarget}
        busy={fetching}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
      />

      <SnackBarDuet
        status={createStatus}
        closeSuccess={() => setCreateStatus(EMPTY_STATUS)}
        closeFailed={() => setCreateStatus(EMPTY_STATUS)}
        successPrompt={accountLabels.createStatus.success[language]}
        failedPrompt={accountLabels.createStatus.failed[language]}
      />
      <SnackBarDuet
        status={deleteStatus}
        closeSuccess={() => setDeleteStatus(EMPTY_STATUS)}
        closeFailed={() => setDeleteStatus(EMPTY_STATUS)}
        successPrompt={userDetailsLabels.deleteStatus.success[language]}
        failedPrompt={userDetailsLabels.deleteStatus.failed[language]}
      />
      <SnackBarDuet
        status={updateStatus}
        closeSuccess={() => setUpdateStatus(EMPTY_STATUS)}
        closeFailed={() => setUpdateStatus(EMPTY_STATUS)}
        successPrompt={userDetailsLabels.updateStatus.success[language]}
        failedPrompt={userDetailsLabels.updateStatus.failed[language]}
      />
      <SnackBarDuet
        status={passwordStatus}
        closeSuccess={() => setPasswordStatus(EMPTY_STATUS)}
        closeFailed={() => setPasswordStatus(EMPTY_STATUS)}
        successPrompt={accountLabels.passwordStatus.success[language]}
        failedPrompt={accountLabels.passwordStatus.failed[language]}
      />
      <SnackBarDuet
        status={adminStatus}
        closeSuccess={() => setAdminStatus(EMPTY_STATUS)}
        closeFailed={() => setAdminStatus(EMPTY_STATUS)}
        successPrompt={
          adminWasGranted
            ? userDetailsLabels.addAsAdminStatus.success[language]
            : userDetailsLabels.removeAsAdminStatus.success[language]
        }
        failedPrompt={
          adminWasGranted
            ? userDetailsLabels.addAsAdminStatus.failed[language]
            : userDetailsLabels.removeAsAdminStatus.failed[language]
        }
      />
    </ClbsAppBar>
  );
};

/* ------------------------------------------------------------------ */
/* Create panel                                                        */
/* ------------------------------------------------------------------ */

const CreatePanel = ({ classes, language, groups, onCreate, busy }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const reset = () => {
    setForm(EMPTY_FORM);
    setErrors({});
    setShowPassword(false);
  };

  const validate = () => {
    const next = {};
    // Not checked for email shape: this is the login ID and is regularly
    // something like psg01. Mail delivery to it is allowed to just fail.
    if (!form.email.trim()) next.email = accountLabels.emailRequired[language];
    if (!form.group.trim()) next.group = accountLabels.groupRequired[language];
    if (!form.password) next.password = accountLabels.passwordRequired[language];
    else if (passwordBytes(form.password) > MAX_PASSWORD_BYTES)
      next.password = accountLabels.passwordTooLong[language];
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    const created = await onCreate(form);
    if (created) reset();
  };

  return (
    <Accordion defaultExpanded className={classes.card}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Avatar className={classes.createIcon}>
          <AddIcon />
        </Avatar>
        <div>
          <Typography variant="h6">
            {accountLabels.createAccount[language]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {accountLabels.createSubtitle[language]}
          </Typography>
        </div>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ pt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={accountLabels.englishName[language]}
              variant="outlined"
              fullWidth
              value={form.english_name}
              onChange={(e) => setField("english_name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label={accountLabels.chineseName[language]}
              variant="outlined"
              fullWidth
              value={form.chinese_name}
              onChange={(e) => setField("chinese_name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              required
              label={accountLabels.email[language]}
              variant="outlined"
              fullWidth
              value={form.email}
              error={Boolean(errors.email)}
              helperText={errors.email || accountLabels.emailHint[language]}
              onChange={(e) => setField("email", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              freeSolo
              options={groups}
              value={form.group}
              onInputChange={(_, value) => setField("group", value || "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={accountLabels.group[language]}
                  variant="outlined"
                  error={Boolean(errors.group)}
                  helperText={errors.group || accountLabels.groupHint[language]}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={5}>
            <TextField
              required
              label={accountLabels.password[language]}
              variant="outlined"
              fullWidth
              type={showPassword ? "text" : "password"}
              value={form.password}
              error={Boolean(errors.password)}
              helperText={errors.password || accountLabels.passwordHint[language]}
              onChange={(e) => setField("password", e.target.value)}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          showPassword
                            ? accountLabels.hidePassword[language]
                            : accountLabels.showPassword[language]
                        }
                      >
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="outlined"
              startIcon={<AutorenewIcon />}
              // Reveal it too: a generated password the admin cannot read is
              // one they cannot pass on.
              onClick={() => {
                setField("password", generatePassword());
                setShowPassword(true);
                setErrors((prev) => ({ ...prev, password: undefined }));
              }}
              sx={{ mt: { sm: "9px" } }}
            >
              {accountLabels.generate[language]}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm="auto">
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.is_admin}
                  onChange={(e) => setField("is_admin", e.target.checked)}
                />
              }
              label={
                <>
                  {accountLabels.grantAdmin[language]}
                  <Typography variant="caption" display="block" color="text.secondary">
                    {accountLabels.grantAdminHint[language]}
                  </Typography>
                </>
              }
            />
          </Grid>
          <Grid item xs={12} sm sx={{ textAlign: { sm: "right" } }}>
            <Button color="inherit" onClick={reset} sx={{ mr: 1 }}>
              {accountLabels.clear[language]}
            </Button>
            <Button variant="contained" onClick={submit} disabled={busy}>
              {accountLabels.create[language]}
            </Button>
          </Grid>
        </Grid>

        <div className={classes.note}>
          <InfoOutlinedIcon fontSize="small" />
          <span>{accountLabels.passwordNote[language]}</span>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

/* ------------------------------------------------------------------ */
/* Accounts list                                                       */
/* ------------------------------------------------------------------ */

const AccountsList = ({
  classes,
  language,
  users,
  groups,
  filter,
  setFilter,
  currentEmail,
  justCreated,
  onManage,
  onDelete,
  loading,
}) => {
  const search = filter.search.trim().toLowerCase();
  const visible = users.filter((u) => {
    if (filter.group && u.group !== filter.group) return false;
    if (!search) return true;
    return [u.email, u.english_name, u.chinese_name]
      .filter(Boolean)
      .some((field) => field.toLowerCase().includes(search));
  });

  const byGroup = visible.reduce((acc, user) => {
    const key = user.group || "—";
    (acc[key] = acc[key] || []).push(user);
    return acc;
  }, {});

  return (
    <Card className={classes.card}>
      <div className={classes.listHead}>
        <Typography variant="h6" className={classes.listTitle}>
          {accountLabels.existingAccounts[language]}
          <Typography component="span" color="text.secondary">
            {` · ${users.length}`}
          </Typography>
        </Typography>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="group-filter-label">
            {accountLabels.allGroups[language]}
          </InputLabel>
          <Select
            labelId="group-filter-label"
            label={accountLabels.allGroups[language]}
            value={filter.group}
            onChange={(e) =>
              setFilter((prev) => ({ ...prev, group: e.target.value }))
            }
          >
            <MenuItem value="">{accountLabels.allGroups[language]}</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group} value={group}>
                {group}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder={accountLabels.searchPlaceholder[language]}
          value={filter.search}
          onChange={(e) =>
            setFilter((prev) => ({ ...prev, search: e.target.value }))
          }
          slotProps={{
            input: { startAdornment: <SearchIcon color="disabled" sx={{ mr: 1 }} /> },
          }}
        />
      </div>
      <Divider />

      {loading ? (
        <div className={classes.spinnerWrap}>
          <CircularProgress />
        </div>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell}>
                  {accountLabels.email[language]}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {accountLabels.name[language]}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {accountLabels.role[language]}
                </TableCell>
                <TableCell className={classes.tableCell} align="right">
                  {accountLabels.actions[language]}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {accountLabels.noAccounts[language]}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {Object.keys(byGroup)
                .sort((a, b) => a.localeCompare(b))
                .map((group) => (
                  <React.Fragment key={group}>
                    <TableRow className={classes.groupRow}>
                      <TableCell colSpan={4} className={classes.groupCell}>
                        {`${group} · ${byGroup[group].length} ${
                          byGroup[group].length === 1
                            ? accountLabels.account[language]
                            : accountLabels.accounts[language]
                        }`}
                      </TableCell>
                    </TableRow>
                    {byGroup[group]
                      .sort((a, b) => a.email.localeCompare(b.email))
                      .map((user) => {
                        const isSelf = user.email === currentEmail;
                        return (
                          <TableRow
                            key={user.id}
                            className={
                              user.email === justCreated
                                ? classes.newRow
                                : isSelf
                                ? classes.selfRow
                                : undefined
                            }
                          >
                            <TableCell className={classes.tableCell}>
                              {user.email}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {user.english_name}
                              {user.chinese_name && (
                                <div className={classes.chineseName}>
                                  {user.chinese_name}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <Chip
                                size="small"
                                label={
                                  user.is_admin
                                    ? accountLabels.roleAdmin[language]
                                    : accountLabels.roleUser[language]
                                }
                                color={user.is_admin ? "primary" : "default"}
                              />
                              {isSelf && (
                                <Chip
                                  size="small"
                                  label={accountLabels.you[language]}
                                  color="secondary"
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                              {user.email === justCreated && (
                                <Chip
                                  size="small"
                                  label={accountLabels.justCreated[language]}
                                  color="success"
                                  sx={{ ml: 0.5 }}
                                />
                              )}
                            </TableCell>
                            <TableCell className={classes.tableCell} align="right">
                              <Button
                                size="small"
                                startIcon={<ManageAccountsIcon />}
                                onClick={() => onManage(user)}
                              >
                                {accountLabels.manage[language]}
                              </Button>
                              {isSelf ? (
                                <Tooltip
                                  title={accountLabels.cannotDeleteSelf[language]}
                                >
                                  <span>
                                    <IconButton size="small" disabled>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onDelete(user)}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Manage dialog — group, password, admin                              */
/* ------------------------------------------------------------------ */

const ManageDialog = ({
  classes,
  language,
  user,
  groups,
  currentEmail,
  busy,
  onClose,
  onSaveGroup,
  onSetPassword,
  onToggleAdmin,
}) => {
  const [group, setGroup] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setGroup(user?.group || "");
    // Never carry a typed password across to the next account opened.
    setPassword("");
    setPasswordError("");
    setShowPassword(false);
  }, [user]);

  const submitPassword = async () => {
    if (passwordBytes(password) > MAX_PASSWORD_BYTES) {
      setPasswordError(accountLabels.passwordTooLong[language]);
      return;
    }
    const saved = await onSetPassword(user.email, password);
    if (saved) {
      setPassword("");
      setShowPassword(false);
    }
  };

  if (!user) return null;
  const isSelf = user.email === currentEmail;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{accountLabels.manageTitle[language]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" gutterBottom>
          {user.english_name} {user.chinese_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {user.email}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="overline" color="text.secondary" display="block">
          {accountLabels.changeGroup[language]}
        </Typography>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs>
            <Autocomplete
              freeSolo
              options={groups}
              value={group}
              onInputChange={(_, value) => setGroup(value || "")}
              renderInput={(params) => (
                <TextField {...params} size="small" variant="outlined" />
              )}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              disabled={busy || !group.trim() || group === user.group}
              onClick={() => onSaveGroup(user.email, group.trim())}
            >
              {accountLabels.save[language]}
            </Button>
          </Grid>
        </Grid>

        <Typography variant="overline" color="text.secondary" display="block">
          {accountLabels.passwordSection[language]}
        </Typography>
        <Grid container spacing={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <Grid item xs>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              type={showPassword ? "text" : "password"}
              label={accountLabels.newPassword[language]}
              value={password}
              error={Boolean(passwordError)}
              helperText={passwordError || accountLabels.newPasswordHint[language]}
              onChange={(e) => {
                setPassword(e.target.value);
                setPasswordError("");
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip
                        title={
                          showPassword
                            ? accountLabels.hidePassword[language]
                            : accountLabels.showPassword[language]
                        }
                      >
                        <IconButton
                          size="small"
                          edge="end"
                          onClick={() => setShowPassword((prev) => !prev)}
                        >
                          {showPassword ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              startIcon={<AutorenewIcon />}
              onClick={() => {
                setPassword(generatePassword());
                setShowPassword(true);
                setPasswordError("");
              }}
            >
              {accountLabels.generate[language]}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              disabled={busy || !password}
              onClick={submitPassword}
            >
              {accountLabels.setPassword[language]}
            </Button>
          </Grid>
        </Grid>

        <Typography variant="overline" color="text.secondary" display="block">
          {accountLabels.adminSection[language]}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(user.is_admin)}
              disabled={busy || isSelf}
              onChange={(e) => onToggleAdmin(user.email, e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" color="text.secondary">
              {user.is_admin
                ? accountLabels.adminOn[language]
                : accountLabels.adminOff[language]}
            </Typography>
          }
        />
        {isSelf && (
          <Typography variant="caption" display="block" color="text.secondary">
            {accountLabels.cannotDeleteSelf[language]}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{accountLabels.close[language]}</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ */
/* Delete dialog — type-to-confirm                                     */
/* ------------------------------------------------------------------ */

const DeleteDialog = ({ classes, language, user, busy, onClose, onConfirm }) => {
  const [typed, setTyped] = useState("");

  useEffect(() => {
    setTyped("");
  }, [user]);

  if (!user) return null;
  const matches = typed.trim() === user.email;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{accountLabels.deleteTitle[language]}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          {accountLabels.deleteLead[language]} <b>{user.email}</b>.{" "}
          {accountLabels.deleteIrreversible[language]}
          <div className={classes.dangerBox}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              {accountLabels.whatGetsErased[language]}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>
                {accountLabels.erasedLogin[language]}{" "}
                <b>
                  {user.english_name} {user.chinese_name}
                </b>
              </li>
              <li>{accountLabels.erasedAnswers[language]}</li>
              <li>{accountLabels.erasedFiles[language]}</li>
            </ul>
          </div>
          {accountLabels.typeToConfirm[language]}
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          margin="dense"
          variant="outlined"
          label={accountLabels.emailAddress[language]}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{assessmentLabels.cancel[language]}</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!matches || busy}
          onClick={() => onConfirm(user.email)}
        >
          {userDetailsLabels.delete[language]}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PermissionDenied = () => (
  <Card sx={{ maxWidth: 600, margin: "40px auto" }}>
    <CardContent>
      <Typography variant="h5" align="center" gutterBottom>
        Oops!
      </Typography>
      <Typography align="center" color="text.secondary">
        Sorry, you do not have admin permission to view this page
      </Typography>
    </CardContent>
  </Card>
);

export default AccountManagement;
